/**
 * @fileoverview Consent-based GTM loader for main site.
 *
 * Loads Google Tag Manager (GTM) only after the Usercentrics Statistik category is accepted.
 * The category label in the banner is Statistik. Its settings slug is `marketing`.
 * Works in conjunction with `header.php` which initializes `dataLayer` early to queue events.
 * That array stays on the page. It does not request Google.
 *
 * @see README.md For detailed documentation, architecture, and troubleshooting
 * @see site/plugins/generic/snippets/header.php For early dataLayer initialization
 */

(function () {
    'use strict';

    const GTM_CONTAINER_ID = 'GTM-TGKN2LWF';
    const DATA_LAYER_NAME = 'dataLayer';
    // Banner label "Statistik". Do not use "essential": that category is granted before a choice.
    const STATISTICS_CATEGORY_SLUG = 'marketing';
    let gtmLoaded = false;

    /**
     * Checks if GTM is already loaded.
     *
     * Note: Checks `window.google_tag_manager` (not `dataLayer`) because `dataLayer`
     * is initialized early in `header.php`, so it would always exist.
     *
     * @returns {boolean} True if GTM is already loaded
     */
    function isGTMLoaded() {
        return (
            gtmLoaded ||
            window.google_tag_manager !== undefined ||
            document.querySelector('script[src*="googletagmanager.com/gtm.js"]') !== null
        );
    }

    /**
     * Retrieves whether Statistik consent allows GTM to load.
     *
     * Only the Statistik category counts. Essential is accepted before the visitor
     * chooses, and the GTM service is still in that category until it is moved in
     * Usercentrics. Either check would load gtm.js before consent.
     *
     * @returns {Promise<boolean>} True when Statistik is fully accepted
     */
    async function getConsentStatus() {
        if (!window.__ucCmp) {
            return false;
        }

        try {
            const details = await window.__ucCmp.getConsentDetails();
            const statistics = details.categories && details.categories[STATISTICS_CATEGORY_SLUG];

            return statistics?.state === 'ALL_ACCEPTED';
        } catch (error) {
            // eslint-disable-next-line no-console
            console.warn('Error checking Usercentrics consent status:', error);
            return false;
        }
    }

    /**
     * Loads Google Tag Manager asynchronously.
     * Inserts script before first existing script tag (matches Google's pattern).
     *
     * @returns {void}
     */
    function loadGTM() {
        if (isGTMLoaded()) {
            return;
        }

        // Ensure dataLayer exists (may already be initialized in header.php)
        window[DATA_LAYER_NAME] = window[DATA_LAYER_NAME] || [];

        // Push gtm.start event - ensures GTM processes queued events when it loads
        window[DATA_LAYER_NAME].push({
            'gtm.start': new Date().getTime(),
            event: 'gtm.js'
        });

        // Create and inject GTM script asynchronously
        const script = document.createElement('script');
        script.async = true;
        script.src = 'https://www.googletagmanager.com/gtm.js?id=' + GTM_CONTAINER_ID;

        const firstScript = document.getElementsByTagName('script')[0];
        if (firstScript && firstScript.parentNode) {
            firstScript.parentNode.insertBefore(script, firstScript);
        } else {
            document.head.appendChild(script);
        }

        gtmLoaded = true;
    }

    /**
     * Listens for Usercentrics CMP events and loads GTM when Statistik is accepted.
     * Handles ACCEPT_ALL, DENY_ALL, and SAVE event types.
     *
     * @returns {void}
     */
    function listenToUcCmpEvent() {
        window.addEventListener('UC_UI_CMP_EVENT', async (event) => {
            const eventTypes = ['ACCEPT_ALL', 'DENY_ALL', 'SAVE'];

            if (event.detail?.type && eventTypes.includes(event.detail.type)) {
                const consentStatus = await getConsentStatus();
                if (consentStatus && !isGTMLoaded()) {
                    loadGTM();
                }
            }
        });
    }

    /**
     * Initializes the GTM loader.
     * Polls for Usercentrics CMP availability (max 5 seconds), then checks consent
     * or sets up event listener for future consent changes.
     *
     * @returns {Promise<void>}
     */
    async function init() {
        if (isGTMLoaded()) {
            return;
        }

        let attempts = 0;
        const maxAttempts = 50; // 5 seconds max wait (50 × 100ms)

        const checkUsercentrics = async () => {
            if (window.__ucCmp) {
                const consentStatus = await getConsentStatus();
                if (consentStatus) {
                    loadGTM();
                } else {
                    listenToUcCmpEvent();
                }
            } else if (attempts < maxAttempts) {
                attempts++;
                setTimeout(checkUsercentrics, 100);
            } else {
                listenToUcCmpEvent();
            }
        };

        setTimeout(checkUsercentrics, 100);
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
